import { useState, useEffect, useMemo } from 'react';
import { Pane, Heading, Tablist, Tab, AnnotationIcon, CalendarIcon, ClipboardIcon, PeopleIcon, PaperclipIcon, BankAccountIcon, EditIcon } from 'evergreen-ui';
import BulletinBoard from '../components/BulletinBoard';
import Communications from '../components/Communications';
import Archive from '../components/Archive';
import Diary from '../components/Diary';
import Guests from '../components/Guests';
import Volunteers from '../components/Volunteers';
import TurniOperatori from '../components/TurniOperatori';
import CollaborativeMembers from '../components/CollaborativeMembers';
import PagamentiDirettore from '../components/Pagamenti/PagamentiDirettore';
import PagamentiCollaboratore from '../components/Pagamenti/PagamentiCollaboratore';
import Rubrica from '../components/Rubrica';
import { useAuth } from '../services/authContext';
import { RoleProvider } from '../contexts/RoleContext';
import { fetchCommunications, fetchGuests, fetchDiaryEntries, fetchCollaborators } from '../services/commonServices';

const AREA_LABELS = {
  direttore: 'Area direttore',
  educatore: 'Area educatori',
  operatore: 'Area operatori',
};

const DashboardPage = () => {
  const { user } = useAuth();
  const role = user?.role ?? 'operatore';

  const isDirector = role === 'direttore';
  const isEducator = role === 'educatore';
  const isOperator = role === 'operatore';
  const isAdvanced = !isDirector && !!user?.is_advanced;
  const effectiveIsDirector = isDirector || isAdvanced;
  const isRestrictedView = !isDirector && !!user?.restricted_view;

  const [announcements, setAnnouncements] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [guests, setGuests] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [diaryEntries, setDiaryEntries] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const activeGuests = useMemo(() => guests.filter(g => Number(g.is_active) !== 0), [guests]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const common = await Promise.all([
          fetchCommunications(),
          fetchGuests(),
          fetchDiaryEntries(),
        ]);
        if (!mounted) return;
        setCommunications(common[0]);
        setGuests(common[1]);
        setDiaryEntries(common[2] || []);

        // Gli allegati non si precaricano: l'Archivio li carica da archive.php per sezione
        if (isDirector || isAdvanced) {
          const collaboratorsData = await fetchCollaborators();
          if (!mounted) return;
          setCollaborators(collaboratorsData);
        }
      } catch (error) {
        if (!mounted) return;
        console.error('Errore nel caricamento dei dati:', error);
      }
    };

    fetchData();
    return () => { mounted = false; };
  }, [isDirector, isEducator, isAdvanced]);

  const allTabs = useMemo(() => [
    {
      label: 'Bacheca',
      icon: ClipboardIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <BulletinBoard
          announcements={announcements}
          setAnnouncements={setAnnouncements}
        />
      ),
    },
    {
      label: 'Comunicazioni',
      icon: AnnotationIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <Communications
          communications={communications}
          setCommunications={setCommunications}
          collaborators={collaborators}
        />
      ),
    },
    {
      label: 'Collaboratori',
      icon: PeopleIcon,
      roles: ['direttore'],
      component: (
        <CollaborativeMembers
          collaborators={collaborators}
          setCollaborators={setCollaborators}
          isRealDirector={isDirector}
        />
      ),
    },
    {
      label: 'Archivio',
      icon: PaperclipIcon,
      roles: ['direttore', 'educatore'],
      component: (
        <Archive
          attachments={attachments}
          setAttachments={setAttachments}
        />
      ),
    },
    {
      label: 'Ospiti',
      icon: PeopleIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <Guests
          guests={guests}
          setGuests={setGuests}
        />
      ),
    },
    {
      label: 'Volontari',
      icon: PeopleIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <Volunteers
          volunteers={volunteers}
          setVolunteers={setVolunteers}
        />
      ),
    },
    {
      label: 'Turni',
      icon: CalendarIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <TurniOperatori collaborators={collaborators} />
      ),
    },
    {
      label: 'Diario',
      icon: CalendarIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: (
        <Diary
          guests={activeGuests}
          diaryEntries={diaryEntries}
          setDiaryEntries={setDiaryEntries}
        />
      ),
    },
    {
      label: 'Pagamenti',
      icon: BankAccountIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: isDirector
        ? <PagamentiDirettore collaborators={collaborators} />
        : <PagamentiCollaboratore />,
    },
    {
      label: 'Rubrica',
      icon: EditIcon,
      roles: ['operatore', 'direttore', 'educatore'],
      component: <Rubrica />,
    },
  ], [
    announcements, communications, collaborators, attachments,
    guests, volunteers, diaryEntries, activeGuests, isDirector,
  ]);

  const tabs = allTabs.filter((t) =>
    t.roles.includes(role) || (isAdvanced && t.roles.includes('direttore'))
  );

  const safeIndex = selectedIndex < tabs.length ? selectedIndex : 0;

  return (
    <RoleProvider
      isDirector={isDirector}
      isEducator={isEducator}
      isOperator={isOperator}
      isAdvanced={isAdvanced}
      effectiveIsDirector={effectiveIsDirector}
      isRestrictedView={isRestrictedView}
    >
      <Pane padding={32}>
        <Heading size={800} marginBottom={24}>
          {AREA_LABELS[role] ?? 'Area riservata'}
        </Heading>

        <Tablist marginBottom={16} flexBasis={240} flexShrink={0} marginRight={24}>
          {tabs.map((tab, index) => (
            <Tab
              key={tab.label}
              id={tab.label}
              onSelect={() => setSelectedIndex(index)}
              isSelected={safeIndex === index}
            >
              {tab.icon && <tab.icon marginRight={8} />}
              {tab.label}
            </Tab>
          ))}
        </Tablist>

        <Pane>
          {tabs[safeIndex]?.component}
        </Pane>
      </Pane>
    </RoleProvider>
  );
};

export default DashboardPage;
